import React from "react";
import { Box, Typography, Icon, keyframes } from "@mui/material";
import { useTheme } from "@emotion/react";
import smitaHeadshot from "src/assets/smita_headshot.png";
import alexiaHeadshot from "src/assets/alexia_headshot.png";

/**
 * URL of the EdTech Insiders episode featuring Smita + Alexia.
 */
const EPISODE_URL =
  "https://www.buzzsprout.com/1877869/episodes/19425173";

/**
 * Compact podcast badge for the hero. Shows overlapping headshot avatars,
 * an animated audio-waveform, episode title, and a play button linking to
 * the EdTech Insiders episode.
 */
const PodcastBadge = () => {
  const theme = useTheme();

  /**
   * Waveform bar height keyframes. Slight variation gives an organic
   * audio-visualizer feel.
   */
  const bar = keyframes`
    0%, 100% { transform: scaleY(0.3); }
    50%      { transform: scaleY(1); }
  `;

  /**
   * Bar count differs by breakpoint. Mobile gets 4 tighter bars so the
   * waveform doesn't force the pill to overflow; desktop gets the full 7.
   */
  const waveBarsMobile = [0, 1, 2, 3];
  const waveBarsDesktop = [0, 1, 2, 3, 4, 5, 6];

  return (
    <Box
      component="a"
      href={EPISODE_URL}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.75,
        px: 1.25,
        py: 1,
        pr: 2,
        bgcolor: theme.palette.paper.primary,
        border: `1px solid ${theme.palette.text.tertiary}`,
        borderRadius: 999,
        textDecoration: "none",
        color: "inherit",
        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
        },
      }}
    >
      {/* Overlapping avatars */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={smitaHeadshot}
          alt="Smita Saxena"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            objectFit: "cover",
            border: `2px solid ${theme.palette.paper.primary}`,
            zIndex: 2,
          }}
        />
        <Box
          component="img"
          src={alexiaHeadshot}
          alt="Alexia Lewis, Algebra I Teacher at KIPP Polaris"
          title="Alexia Lewis, Algebra I Teacher at KIPP Polaris"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            objectFit: "cover",
            ml: -1.5,
            border: `2px solid ${theme.palette.paper.primary}`,
            zIndex: 1,
          }}
        />
      </Box>

      {/* Title + subtitle */}
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          EdTech Insiders
        </Typography>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: theme.palette.text.primary,
            mt: 0.5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Smita & Alexia on Maestro
        </Typography>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            color: theme.palette.text.secondary,
            mt: 0.25,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Founder + Algebra I Teacher, KIPP
        </Typography>
      </Box>

      {/* Waveform — 4 tight bars on xs, 7 wider bars on sm+ */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "center",
          gap: 0.375,
          height: 18,
          ml: 0.25,
        }}
      >
        {waveBarsMobile.map((i) => (
          <Box
            key={i}
            sx={{
              width: 2.5,
              height: 18,
              borderRadius: 999,
              bgcolor: theme.palette.success.main,
              transformOrigin: "center",
              animation: `${bar} 1.1s ease-in-out infinite`,
              animationDelay: `${i * 0.18}s`,
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 0.5,
          height: 20,
          ml: 0.5,
        }}
      >
        {waveBarsDesktop.map((i) => (
          <Box
            key={i}
            sx={{
              width: 3,
              height: 20,
              borderRadius: 999,
              bgcolor: theme.palette.success.main,
              transformOrigin: "center",
              animation: `${bar} 1.1s ease-in-out infinite`,
              animationDelay: `${(i % 4) * 0.15}s`,
            }}
          />
        ))}
      </Box>

      {/* Play button */}
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          bgcolor: theme.palette.success.main,
          color: theme.palette.paper.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{ fontSize: 22, ml: 0.25 }}
        >
          play_arrow
        </Icon>
      </Box>
    </Box>
  );
};

export default PodcastBadge;
